import { ActiveSearchTags } from '@/components/ActiveSearchTags';
import { CollapsableSearch } from '@/components/CollapsableSearch';
import { ErrorBoundary } from '@/components/Rollbar';
import { SuspenseLoading } from '@/components/SuspenseErrorBoundary/fallback';
import {
  ACTION_BUTTONS_GAP,
  EMPTY_PAYEE_ARRAY,
  EXCLUDED_ACTIVE_TAG_FIELDS,
  INVOICE_REPORTS_ALWAYS_SHOW_FIELDS,
} from '@/features/invoice_reports/constants';
import type { InvoiceReportsSearchQuery } from '@/features/invoice_reports/types';
import { shouldShowViewerField } from '@/features/invoice_reports/utils/shouldShowViewerField';
import { useAxisAuth2ndApiFeatureFlag } from '@/hooks/feature_flag/useAxisAuth2ndApiFeatureFlag';
import { useApPayeeTransformer } from '@/hooks/invoice_reports/useApPayeeTransformer';
import { useInvoiceValueRetrieval } from '@/hooks/invoice_reports/useInvoiceValueRetrieval';
import { usePaymentStatusTransformer } from '@/hooks/invoice_reports/usePaymentStatusTransformer';
import { useShowSearchValue } from '@/hooks/invoice_reports/useShowSearchValue';
import { useGetFlags } from '@/hooks/useFeatureFlag';
import { useTranslation } from '@/i18n';
import type { OfficeInvoiceReportSettings } from 'ap-openapi';
import classnames from 'classnames/bind';
import {
  FC,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { ErrorFallback } from './components/ErrorFallback';
import { SearchForm } from './components/SearchForm';
import {
  InvoiceReportsSearchFormData,
  useAgentApplicantInitialData,
  useAgentApplicantTransformer,
  useApPayeeInitialData,
  useConfigForm,
  useInvoiceReportsFieldOrders,
  useOfficeMembersInitialData,
  useSearchForm,
} from './hooks';
import styles from './Search.module.scss';

const cx = classnames.bind(styles);

interface SearchProps {
  defaultValues?: InvoiceReportsSearchFormData;
  onSearch: (data: InvoiceReportsSearchQuery) => void;
  searchQuery?: InvoiceReportsSearchQuery;
  isLoading?: boolean;
  officeInvoiceReportSettings?: OfficeInvoiceReportSettings;
}

export const InnerSearch: FC<SearchProps> = memo(
  ({
    defaultValues,
    onSearch,
    searchQuery,
    isLoading,
    officeInvoiceReportSettings,
  }) => {
    const { t } = useTranslation();
    const form = useForm<InvoiceReportsSearchFormData>({
      defaultValues,
      mode: 'all',
    });
    const canAgentEntry =
      officeInvoiceReportSettings?.office_member_setting?.can_agent_entry ??
      false;

    const payeeIds = useMemo(
      () => defaultValues?.ap_payee_ids ?? EMPTY_PAYEE_ARRAY,
      [defaultValues?.ap_payee_ids]
    );

    const agentApplicantIds = useMemo(
      () => defaultValues?.agent_applicant_office_member_ids ?? [],
      [defaultValues?.agent_applicant_office_member_ids]
    );

    const flagAxisAuth2ndApi = useAxisAuth2ndApiFeatureFlag();
    const showViewerField = shouldShowViewerField(
      flagAxisAuth2ndApi,
      officeInvoiceReportSettings
    );
    // Applicant field is shown when proxy entry is allowed or the viewer setting is on
    const showApplicantField = canAgentEntry || showViewerField;
    const viewerIds = useMemo(
      () => defaultValues?.viewer_ids ?? [],
      [defaultValues?.viewer_ids]
    );

    const { options: payeeOptions, isLoaded: payeeOptionsLoaded } =
      useApPayeeInitialData(payeeIds);
    const { options: viewerOptions } = useOfficeMembersInitialData(viewerIds);
    const {
      options: agentApplicantOptions,
      isLoaded: agentApplicantOptionsLoaded,
    } = useAgentApplicantInitialData(agentApplicantIds);

    // Remove stale proxy applicant IDs (removed from the system) from the form
    // value after initial data confirms which IDs still exist.
    // Mirrors the ExItemSelect pattern: run once when loaded, check value against
    // resolved options, clear any IDs that were not returned by the API.
    const agentApplicantOptionsLoadedRef = useRef(false);
    const selectedAgentApplicantIds = useWatch({
      name: 'agent_applicant_office_member_ids',
      control: form.control,
    });
    useEffect(() => {
      if (
        !agentApplicantOptionsLoaded ||
        agentApplicantOptionsLoadedRef.current
      )
        return;
      agentApplicantOptionsLoadedRef.current = true;

      const ids = Array.isArray(selectedAgentApplicantIds)
        ? selectedAgentApplicantIds
        : [];
      if (!ids.length) return;

      const validIds = ids.filter((id) =>
        agentApplicantOptions.some((opt) => String(opt.value) === id)
      );
      if (validIds.length < ids.length) {
        form.setValue('agent_applicant_office_member_ids', validIds, {
          shouldValidate: true,
        });
      }
    }, [
      agentApplicantOptionsLoaded,
      agentApplicantOptions,
      selectedAgentApplicantIds,
      form,
    ]);

    const { customProps, checkLengthValidationRules } = useConfigForm();
    const { handleReset } = useSearchForm({ form });

    // Use primitive dependencies for sort fields
    const sortFields = defaultValues?.sort_fields;
    const sortOrders = defaultValues?.sort_orders;

    const onHandleSearch = useCallback(
      (data: InvoiceReportsSearchFormData) => {
        const {
          status,
          mf_file,
          payment_status,
          agent_applicant_office_member_ids,
          viewer_ids,
          ...rest
        } = data;

        onSearch({
          ...rest,
          status: status === '' || status === null ? undefined : status,
          mf_file: mf_file === '' || mf_file === null ? undefined : mf_file,
          payment_status:
            payment_status === '' || payment_status === null
              ? undefined
              : payment_status,
          ...(showApplicantField &&
            agent_applicant_office_member_ids?.length && {
              agent_applicant_office_member_ids,
            }),
          // Drop a stale viewer filter (e.g. from a bookmarked URL) once the
          // field is hidden, so it can't silently keep filtering results.
          ...(showViewerField &&
            viewer_ids?.length && {
              viewer_ids,
            }),
          sort_fields: sortFields,
          sort_orders: sortOrders,
        });
      },
      [onSearch, sortFields, sortOrders, showApplicantField, showViewerField]
    );

    const keepSpotPayeeFilter = useGetFlags(
      'INVOICE_REPORTS_KEEP_SPOT_PAYEE_FILTER'
    );
    const instantPayeeId =
      keepSpotPayeeFilter &&
      officeInvoiceReportSettings?.instant_payee_setting?.enabled
        ? officeInvoiceReportSettings?.instant_payee_setting
            ?.instant_payee_search_option?.id
        : undefined;

    const payeeOptionsLoadedRef = useRef(false);
    const selectedPayeeIds = useWatch({
      name: 'ap_payee_ids',
      control: form.control,
    });
    useEffect(() => {
      if (!payeeOptionsLoaded || payeeOptionsLoadedRef.current) return;
      payeeOptionsLoadedRef.current = true;

      const ids = Array.isArray(selectedPayeeIds) ? selectedPayeeIds : [];
      if (!ids.length) return;

      const validIds = ids.filter(
        (id) =>
          id === instantPayeeId ||
          payeeOptions.some((opt) => String(opt.value) === id)
      );
      if (validIds.length < ids.length) {
        form.setValue('ap_payee_ids', validIds, { shouldValidate: true });
      }
    }, [
      payeeOptionsLoaded,
      payeeOptions,
      selectedPayeeIds,
      instantPayeeId,
      form,
    ]);

    /** Exclude agent tag when the applicant field is hidden  */
    const excludeFields = useMemo(() => {
      const fields = [...EXCLUDED_ACTIVE_TAG_FIELDS];
      if (!showApplicantField) {
        fields.push('agent_applicant_office_member_ids');
      }
      return fields;
    }, [showApplicantField]);

    const { FIELD_ORDERS } = useInvoiceReportsFieldOrders(showViewerField);
    const paymentStatusTransformers = usePaymentStatusTransformer();
    const agentApplicantTransformers = useAgentApplicantTransformer(
      agentApplicantOptions
    );
    const apPayeeTransformers = useApPayeeTransformer(
      payeeOptions,
      officeInvoiceReportSettings
    );
    const { getOfficeMemberValues } = useInvoiceValueRetrieval();
    const viewerTransformers = useMemo(
      () => ({
        viewer_office_member_ids: (v: unknown) =>
          getOfficeMemberValues(
            Array.isArray(v) ? v.map(String) : [],
            viewerOptions
          ),
      }),
      [getOfficeMemberValues, viewerOptions]
    );
    const customTransformers = useMemo(
      () => ({
        ...paymentStatusTransformers,
        ...agentApplicantTransformers,
        ...apPayeeTransformers,
        ...viewerTransformers,
      }),
      [
        paymentStatusTransformers,
        agentApplicantTransformers,
        apPayeeTransformers,
        viewerTransformers,
      ]
    );

    // Initialize to 220 so --action-buttons-space = 220 + 8 = 228px on first render,
    // matching the CSS fallback and preventing the section title from overlapping action buttons.
    const [actionButtonsWidth, setActionButtonsWidth] = useState(220);
    const headerStyle = useMemo<Record<string, string>>(
      () => ({
        '--action-buttons-space': `${
          actionButtonsWidth + ACTION_BUTTONS_GAP
        }px`,
      }),
      [actionButtonsWidth]
    );

    const { searchValuesShow } = useShowSearchValue({
      searchQuery,
      fieldOrders: FIELD_ORDERS,
      payeeOptions: payeeOptions,
      excludeFields,
      customTransformers,
      alwaysShowFields: INVOICE_REPORTS_ALWAYS_SHOW_FIELDS,
    });

    // Memoize the render callback for CollapsableSearch
    const renderSearchForm = useCallback(
      () => (
        <SearchForm
          agentApplicantDefaultOptions={agentApplicantOptions}
          appPayeeDefaultOptions={payeeOptions}
          showApplicantField={showApplicantField}
          customProps={customProps}
          checkLengthValidationRules={checkLengthValidationRules}
          form={form}
          onSubmit={onHandleSearch}
          onReset={handleReset}
          isLoading={isLoading}
          onActionButtonsWidthChange={setActionButtonsWidth}
          officeInvoiceReportSettings={officeInvoiceReportSettings}
          viewerOptions={viewerOptions}
          showViewerField={showViewerField}
        />
      ),
      [
        agentApplicantOptions,
        payeeOptions,
        showApplicantField,
        customProps,
        checkLengthValidationRules,
        form,
        onHandleSearch,
        handleReset,
        isLoading,
        setActionButtonsWidth,
        officeInvoiceReportSettings,
        viewerOptions,
        showViewerField,
      ]
    );

    return (
      <div className={cx('search-container')}>
        <div className={cx('header')} style={headerStyle}>
          <CollapsableSearch
            initialOpen
            openTitle={t('invoice_reports.search.collapse_title')}
            closeTitle={t('invoice_reports.search.expand_title')}
            sectionTitleExpandedClassName={cx('section-title-expanded')}
          >
            {renderSearchForm}
          </CollapsableSearch>
        </div>
        <ActiveSearchTags activeSearchValues={searchValuesShow} />
      </div>
    );
  }
);
InnerSearch.displayName = 'InnerSearch';

// Memoized error fallback render function
const renderErrorFallback = ({ error }: { error: Error | null }) => (
  <ErrorFallback error={error} />
);

export const Search: FC<SearchProps> = memo(
  ({
    defaultValues,
    onSearch,
    searchQuery,
    isLoading,
    officeInvoiceReportSettings,
  }) => {
    return (
      <Suspense
        fallback={
          <SuspenseLoading className={cx('search-container-loading')} />
        }
      >
        <ErrorBoundary fallback={renderErrorFallback}>
          <InnerSearch
            defaultValues={defaultValues}
            onSearch={onSearch}
            searchQuery={searchQuery}
            isLoading={isLoading}
            officeInvoiceReportSettings={officeInvoiceReportSettings}
          />
        </ErrorBoundary>
      </Suspense>
    );
  }
);
Search.displayName = 'Search';
